import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import {
  ChannelRoles,
  ChannelStatus,
  IChannel,
  IChannelByParams,
  IChannelCreationDTO,
  IChannelOwner,
} from '../../interfaces/IChannel';
import { IDealer } from '../../interfaces/IDealer';
import { IMessage, IPartialMessage, IReaction } from '../../interfaces/IMessage';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';
import { IRepairOrder } from '../../interfaces/IRepairOrder';
import { ITag } from '../../interfaces/ITag';
import { ChannelsApiService } from './channels-api.service';
import { IntegrationsApiService } from './integrations-api.service';
import { MessagesApiService } from './messages-api.service';
import { TagsApiService } from './tags-api.service';
import { UsersApiService } from './users-api.service';
import { IAttachment } from '../../interfaces/IAttachment';

/**
 * Thin facade over domain-specific messaging API services.
 * Prefer injecting the domain services directly in new code.
 */
@Injectable()
export class MessagingApiService {
  constructor(
    private channelsApi: ChannelsApiService,
    private messagesApi: MessagesApiService,
    private usersApi: UsersApiService,
    private tagsApi: TagsApiService,
    private integrationsApi: IntegrationsApiService
  ) {}

  getChannels() {
    return this.channelsApi.getChannels();
  }

  getChannel(channelUID: string, channelByParams: IChannelByParams, ignoreTags: boolean) {
    return this.channelsApi.getChannel(channelUID, channelByParams, ignoreTags);
  }

  getMessages(channel: IChannel) {
    return this.messagesApi.getMessages(channel);
  }

  sendMessage(message: IPartialMessage): Observable<IMessage> {
    return this.messagesApi.sendMessage(message);
  }

  pinChannel(channel: IChannel, pinned: boolean): Observable<unknown> {
    return this.channelsApi.pinChannel(channel, pinned);
  }

  checkNewMessagesByDate(channel: IChannel, date: Date): Observable<boolean> {
    return this.messagesApi.checkNewMessagesByDate(channel, date);
  }

  changeBotMode(channel: IChannel, value: boolean): Observable<unknown> {
    return this.channelsApi.changeBotMode(channel, value);
  }

  changeChannelEnabledStatus(channel: IChannel, value: boolean): Observable<unknown> {
    return this.channelsApi.changeChannelEnabledStatus(channel, value);
  }

  muteChannel(channel: IChannel, mute: boolean): Observable<unknown> {
    return this.channelsApi.muteChannel(channel, mute);
  }

  getUsersByDefect(): Observable<IMessageableEntity[]> {
    return this.usersApi.getUsersByDefect();
  }

  searchUsers(data: {
    searchTerm: string;
    page: number;
    size: number;
    tags?: ITag[];
    useGroup?: boolean;
    entityUID?: string;
    status?: string;
  }): Observable<IMessageableEntity[]> {
    return this.usersApi.searchUsers(data);
  }

  updateUser(displayName: string, attachments: IAttachment[]): Observable<unknown> {
    return this.usersApi.updateUser(displayName, attachments);
  }

  sendAway(to: Date, userId: string, message: string): Observable<unknown> {
    return this.usersApi.sendAway(to, userId, message);
  }

  cancelAway(): Observable<unknown> {
    return this.usersApi.cancelAway();
  }

  getUser() {
    return this.usersApi.getUser();
  }

  getRepairOrder(orderID: string): Observable<{ repairOrder: IRepairOrder; video: string | null }> {
    return this.integrationsApi.getRepairOrder(orderID);
  }

  getRepairOrderByJobName(job: string) {
    return this.integrationsApi.getRepairOrderByJobName(job);
  }

  getDealers(): Observable<IDealer[]> {
    return this.channelsApi.getDealers();
  }

  selectDealer(dealer: IDealer) {
    return this.channelsApi.selectDealer(dealer);
  }

  createChannel(newChannel: IChannelCreationDTO, members: string[]): Observable<IChannel> {
    return this.channelsApi.createChannel(newChannel, members);
  }

  createChannelByParams(data: IChannelByParams): Observable<IChannel | null> {
    return this.channelsApi.createChannelByParams(data);
  }

  getMemberUID(members: number[]): Observable<string[]> {
    return this.usersApi.getMemberUID(members);
  }

  optIntOutState(enabled: boolean) {
    return this.channelsApi.optIntOutState(enabled);
  }

  deleteMessageById(messageUid: string): Observable<unknown> {
    return this.messagesApi.deleteMessageById(messageUid);
  }

  getTokenVideoCall(channelId: string) {
    return this.integrationsApi.getTokenVideoCall(channelId);
  }

  patchChannelInfo(channel: IChannel, displayName: string) {
    return this.channelsApi.patchChannelInfo(channel, displayName);
  }

  patchChannelPhone(channel: IChannel, newPhone: string, oldPhone: string) {
    return this.channelsApi.patchChannelPhone(channel, newPhone, oldPhone);
  }

  leaveCallRoom(room: string, participantID: string) {
    return this.integrationsApi.leaveCallRoom(room, participantID);
  }

  joinCallRoom(room: string, participantID: string) {
    return this.integrationsApi.joinCallRoom(room, participantID);
  }

  setReadChannel(channel: IChannel, value: boolean): Observable<unknown> {
    return this.channelsApi.setReadChannel(channel, value);
  }

  readAllMessages(): Observable<unknown> {
    return this.channelsApi.readAllMessages();
  }

  changeMessageDeliveryStatus(messageUID: string, value: string): Observable<unknown> {
    return this.messagesApi.changeMessageDeliveryStatus(messageUID, value);
  }

  deleteMemberChannel(members: string[]): Observable<unknown> {
    return this.channelsApi.deleteMemberChannel(members);
  }

  addMembersChannel(members: string[]): Observable<unknown> {
    return this.channelsApi.addMembersChannel(members);
  }

  getMembersChannel(page: number, size: number): Observable<IMessageableEntity[]> {
    return this.channelsApi.getMembersChannel(page, size);
  }

  changeMemberRole(memberUid: string, rol: ChannelRoles): Observable<unknown> {
    return this.channelsApi.changeMemberRole(memberUid, rol);
  }

  leaveChannel(): Observable<unknown> {
    return this.channelsApi.leaveChannel();
  }

  getTags(): Observable<ITag[]> {
    return this.tagsApi.getTags();
  }

  notifyVideoCallGroup(memberUID: string, roomName: string): void {
    this.messagesApi.notifyVideoCallGroupMessage(memberUID, `vc=${roomName}`);
  }

  notifyVideoCallGroupMessage(memberUID: string, message: string) {
    return this.messagesApi.notifyVideoCallGroupMessage(memberUID, message);
  }

  joinChannel(channelUID: string): Observable<unknown> {
    return this.channelsApi.joinChannel(channelUID);
  }

  getChannelMembersPhotos(channel: IChannel): Observable<IMessageableEntity[]> {
    return this.channelsApi.getChannelMembersPhotos(channel);
  }

  getChannelOldMessagesValue(channel: IChannel, startDate: Date): Observable<boolean> {
    return this.messagesApi.getChannelOldMessagesValue(channel, startDate);
  }

  changeChannelStatus(channel: IChannel, value: ChannelStatus): Observable<unknown> {
    return this.channelsApi.changeChannelStatus(channel, value);
  }

  changeChannelOwner(channel: IChannel, owner: IChannelOwner): Observable<unknown> {
    return this.channelsApi.changeChannelOwner(channel, owner);
  }

  changeChannelOrderID(channel: IChannel, orderId: string): Observable<unknown> {
    return this.channelsApi.changeChannelOrderID(channel, orderId);
  }

  sendReaction(reaction: IReaction, channelUID: string, messageUID: string): Observable<unknown> {
    return this.messagesApi.sendReaction(reaction, channelUID, messageUID);
  }

  getUnreads(): Observable<{ count: number }> {
    return this.channelsApi.getUnreads();
  }
}
